import type { Ref } from 'vue'

const FONT_SIZES = [40, 36, 32, 28, 24]
const MAX_LINES = 4
const MAX_ORPHAN_CHARS = 2

function hasWordOverflow (el: HTMLElement): boolean {
  const parent = el.parentElement
  if (!parent) return false

  const original = parent.style.overflowWrap
  parent.style.overflowWrap = 'normal'
  const overflows = parent.scrollWidth > parent.clientWidth
  parent.style.overflowWrap = original
  return overflows
}

function hasOrphan (el: HTMLElement): boolean {
  const rects = Array.from(el.getClientRects())
  if (rects.length <= 1) return false

  const text = el.textContent || ''
  if (!text.trim()) return false

  const totalWidth = rects.reduce((sum, r) => sum + r.width, 0)
  const avgCharWidth = totalWidth / text.length
  const lastRect = rects[rects.length - 1]

  return lastRect.width <= avgCharWidth * (MAX_ORPHAN_CHARS + 0.5)
}

function exceedsMaxLines (el: HTMLElement): boolean {
  return el.getClientRects().length > MAX_LINES
}

function shouldShrink (el: HTMLElement): boolean {
  return hasWordOverflow(el) || exceedsMaxLines(el) || hasOrphan(el)
}

function defaultTaglineSizeFor (text: string): number {
  return text.length > 40 ? FONT_SIZES[2] : text.length > 30 ? FONT_SIZES[1] : FONT_SIZES[0]
}

export function useAutoFitText (spanRef: Ref<HTMLElement | undefined>, text: () => string) {
  const fontSize = ref(defaultTaglineSizeFor(text()))

  async function adjust () {
    if (!spanRef.value) return

    fontSize.value = defaultTaglineSizeFor(text())
    await nextTick()

    while (shouldShrink(spanRef.value)) {
      const idx = FONT_SIZES.indexOf(fontSize.value)
      if (idx >= FONT_SIZES.length - 1) break
      fontSize.value = FONT_SIZES[idx + 1]
      await nextTick()
    }
  }

  watch(text, adjust, { flush: 'post' })
  onMounted(adjust)

  return fontSize
}
